import {
  PLANTS,
  companionsOf,
  incompatiblesOf,
  scheduleForZone,
} from "@/lib/catalog";
import { getClimateZone } from "@/data/climate-zones";
import type { GardenZone, PlantPlacement } from "@/types/garden";
import type { Plant } from "@/types/plants";
import type { GuestGarden } from "@/lib/guest-garden";

/**
 * Builds a ready-to-edit plan from three answers: garden shape, department and
 * experience level.
 *
 * The point is to remove the blank canvas, not to hand out an optimal planting
 * scheme — everything here is a starting point the gardener will move around.
 * Placements therefore respect real spacing but stop well short of filling a
 * bed, leaving obvious room to add more.
 */

/** Placement coordinates are canvas pixels; zones are centimetres. */
const PIXELS_PER_CM = 2;

/** Enough to show intent, few enough to stay editable. */
const MAX_PLANTS_PER_ZONE = 12;

export type TemplateId = "balcon" | "carre" | "familial";
export type Level = "debutant" | "confirme";

type ZoneSpec = {
  name: string;
  x: number;
  y: number;
  width_cm: number;
  height_cm: number;
};

export type GardenTemplate = {
  id: TemplateId;
  label: string;
  summary: string;
  emoji: string;
  width_cm: number;
  height_cm: number;
  zones: ZoneSpec[];
};

export const TEMPLATES: GardenTemplate[] = [
  {
    id: "balcon",
    label: "Balcon ou terrasse",
    summary: "Quelques bacs — 2 m sur 1,2 m",
    emoji: "🪴",
    width_cm: 200,
    height_cm: 120,
    zones: [
      { name: "Grand bac", x: 20, y: 20, width_cm: 80, height_cm: 40 },
      { name: "Bac aromatiques", x: 120, y: 20, width_cm: 60, height_cm: 40 },
      { name: "Jardinière", x: 20, y: 75, width_cm: 160, height_cm: 30 },
    ],
  },
  {
    id: "carre",
    label: "Carré potager",
    summary: "Un carré surélevé — 1,2 m de côté",
    emoji: "🟩",
    width_cm: 240,
    height_cm: 240,
    zones: [
      { name: "Carré nord", x: 55, y: 55, width_cm: 60, height_cm: 60 },
      { name: "Carré est", x: 125, y: 55, width_cm: 60, height_cm: 60 },
      { name: "Carré ouest", x: 55, y: 125, width_cm: 60, height_cm: 60 },
      { name: "Carré sud", x: 125, y: 125, width_cm: 60, height_cm: 60 },
    ],
  },
  {
    id: "familial",
    label: "Potager en pleine terre",
    summary: "Trois planches de culture — 6 m sur 4 m",
    emoji: "🌾",
    width_cm: 600,
    height_cm: 400,
    zones: [
      { name: "Planche 1", x: 80, y: 50, width_cm: 440, height_cm: 80 },
      { name: "Planche 2", x: 80, y: 165, width_cm: 440, height_cm: 80 },
      { name: "Planche 3", x: 80, y: 280, width_cm: 440, height_cm: 80 },
    ],
  },
];

export function getTemplate(id: TemplateId): GardenTemplate {
  return TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[1];
}

/**
 * Crops that forgive mistakes: quick, visible results and no tricky timing.
 * A first plan made of celery and aubergine is a first plan that fails.
 */
const BEGINNER_FRIENDLY = new Set([
  "radis",
  "laitue",
  "courgette",
  "haricot-vert",
  "tomate",
  "basilic",
  "carotte",
  "fraisier",
  "persil",
  "ciboulette",
  "menthe",
  "thym",
  "epinard",
  "capucine",
  "souci",
  "oeillet-inde",
  "pois",
  "concombre",
  "potiron",
]);

/** Departments read from a postal code, including the Corsican split. */
export function departmentFromPostalCode(postal: string): string | null {
  const digits = postal.replace(/\D/g, "");
  if (digits.length !== 5) return null;
  const prefix = digits.slice(0, 2);
  if (prefix === "20") {
    // 200xx–201xx is Corse-du-Sud, the rest Haute-Corse.
    const third = Number(digits[2]);
    return third <= 1 ? "2A" : "2B";
  }
  return prefix;
}

export function climateZoneFor(department: string | null): number {
  return department ? getClimateZone(department) : 5;
}

/**
 * Plants worth sowing or planting around `month` in this climate zone.
 * A two-month look-ahead keeps the plan useful when someone arrives just
 * before a window opens — most people plan a few weeks out.
 */
function seasonalPlants(month: number, zone: number, level: Level): Plant[] {
  const months = [month, (month % 12) + 1, ((month + 1) % 12) + 1];

  const inSeason = PLANTS.filter((plant) => {
    const sowing = scheduleForZone(plant, zone).sowing;
    return months.some((m) => sowing.includes(m));
  });

  const pool =
    level === "debutant"
      ? inSeason.filter((p) => BEGINNER_FRIENDLY.has(p.id))
      : inSeason;

  return pool;
}

/**
 * Perennial herbs and hardy staples, plantable almost year-round and small
 * enough for a planter.
 *
 * Used to widen a thin seasonal pool: in November on the Mediterranean coast
 * the only thing "in season" is an early tomato sowing, which fits no balcony
 * bed. Without this the generator handed out three identical beds of a crop
 * nobody plants in November.
 */
const YEAR_ROUND_FILLERS = [
  "ciboulette",
  "persil",
  "thym",
  "menthe",
  "romarin",
  "sauge",
  "laitue",
  "radis",
  "epinard",
  "ail",
];

function widenPool(pool: Plant[], level: Level, wanted: number): Plant[] {
  if (pool.length >= wanted) return pool;

  const have = new Set(pool.map((p) => p.id));
  const extras = YEAR_ROUND_FILLERS.map((id) => PLANTS.find((p) => p.id === id))
    .filter((p): p is Plant => Boolean(p))
    .filter((p) => !have.has(p.id))
    .filter((p) => level === "confirme" || BEGINNER_FRIENDLY.has(p.id) || p.id === "ail");

  return [...pool, ...extras];
}

/** Sorts so vegetables lead and aromatics/flowers act as companions. */
function byRole(plants: Plant[]): Plant[] {
  const rank: Record<string, number> = {
    legumes: 0,
    fruits: 1,
    aromatiques: 2,
    fleurs: 3,
  };
  return [...plants].sort((a, b) => rank[a.category] - rank[b.category]);
}

/** A crop only belongs in a bed it physically fits in. */
function fitsIn(plant: Plant, zone: ZoneSpec): boolean {
  return plant.spacing_cm <= Math.min(zone.width_cm, zone.height_cm);
}

/**
 * Picks the main crop for one bed.
 *
 * Size comes first — a potiron has no business in a 60 cm square — then family
 * variety, so the rotation advice has something to work with from day one.
 */
function chooseCropForZone(
  pool: Plant[],
  zone: ZoneSpec,
  usedFamilies: Set<string>,
  usedIds: Set<string>,
): Plant | null {
  const ordered = byRole(pool);
  const mains = ordered.filter(
    (p) => p.category === "legumes" || p.category === "fruits",
  );

  // Beds too small for any vegetable still deserve a plant: aromatics fit
  // almost anywhere and are exactly what a balcony planter is for.
  const candidates = [
    mains.filter((p) => fitsIn(p, zone)),
    ordered.filter((p) => fitsIn(p, zone)),
    mains,
    ordered,
  ].find((list) => list.length > 0);
  if (!candidates) return null;

  return (
    candidates.find((p) => !usedFamilies.has(p.family) && !usedIds.has(p.id)) ??
    candidates.find((p) => !usedIds.has(p.id)) ??
    candidates[0]
  );
}

/** A companion that is in season and not antagonistic to the main crop. */
function pickCompanion(main: Plant, pool: Plant[]): Plant | null {
  const banned = new Set(incompatiblesOf(main).map((p) => p.id));
  const friends = companionsOf(main).filter(
    (p) => !banned.has(p.id) && pool.some((s) => s.id === p.id),
  );
  // Aromatics and flowers make the best edge companions: small and useful.
  const compact = friends.filter(
    (p) => p.category === "aromatiques" || p.category === "fleurs",
  );
  return compact[0] ?? friends[0] ?? null;
}

/**
 * Lays plants out inside a bed, spread across its whole surface.
 *
 * The count is capped so the plan stays editable, but the plants are spaced
 * out to use the bed rather than crammed into the first row — a 4 m bed with
 * one dense row and nothing else reads as an unfinished plan. Steps never go
 * below the plant's real spacing.
 */
function fillZone(
  zone: ZoneSpec,
  zoneId: string,
  main: Plant,
  companion: Plant | null,
): PlantPlacement[] {
  const spacing = Math.max(main.spacing_cm, companion?.spacing_cm ?? 0, 15);
  const cols = Math.floor(zone.width_cm / spacing);
  const rows = Math.floor(zone.height_cm / spacing);

  // The bed is narrower than one spacing step. Rather than leave it empty —
  // which reads as a broken plan — put a single plant in the middle and let
  // the gardener decide.
  if (cols < 1 || rows < 1) {
    return [
      {
        id: crypto.randomUUID(),
        zone_id: zoneId,
        plant_id: main.id,
        x: (zone.x + zone.width_cm / 2) * PIXELS_PER_CM,
        y: (zone.y + zone.height_cm / 2) * PIXELS_PER_CM,
      },
    ];
  }

  // Centre the grid in the bed so it never hugs an edge.
  // Shrink the grid to the cap while keeping the bed's proportions, so the
  // plants stay spread out instead of piling into the first rows.
  let usedRows = rows;
  let usedCols = cols;
  if (cols * rows > MAX_PLANTS_PER_ZONE) {
    const ratio = zone.height_cm / zone.width_cm;
    usedRows = Math.min(
      rows,
      Math.max(1, Math.round(Math.sqrt(MAX_PLANTS_PER_ZONE * ratio))),
    );
    usedCols = Math.min(cols, Math.max(1, Math.floor(MAX_PLANTS_PER_ZONE / usedRows)));
  }

  // Steps are at least the real spacing, since usedCols <= cols.
  const stepX = zone.width_cm / usedCols;
  const stepY = zone.height_cm / usedRows;

  const placements: PlantPlacement[] = [];
  const companionCol = companion && usedCols >= 3 ? usedCols - 1 : -1;

  for (let row = 0; row < usedRows; row++) {
    for (let col = 0; col < usedCols; col++) {
      const plant = col === companionCol ? companion! : main;
      const cx = zone.x + col * stepX + stepX / 2;
      const cy = zone.y + row * stepY + stepY / 2;
      placements.push({
        id: crypto.randomUUID(),
        zone_id: zoneId,
        plant_id: plant.id,
        x: cx * PIXELS_PER_CM,
        y: cy * PIXELS_PER_CM,
      });
    }
  }

  return placements;
}

export type StarterAnswers = {
  template: TemplateId;
  postalCode: string;
  level: Level;
  /** Injected in tests; defaults to the current month. */
  month?: number;
};

export type StarterPlan = {
  garden: GuestGarden;
  department: string | null;
  climateZone: number;
  /** Distinct species used, for the confirmation copy. */
  species: Plant[];
};

export function buildStarterPlan(answers: StarterAnswers): StarterPlan {
  const template = getTemplate(answers.template);
  const department = departmentFromPostalCode(answers.postalCode);
  const climateZone = climateZoneFor(department);
  const month = answers.month ?? new Date().getMonth() + 1;

  const pool = widenPool(
    seasonalPlants(month, climateZone, answers.level),
    answers.level,
    template.zones.length + 1,
  );

  const usedFamilies = new Set<string>();
  const usedIds = new Set<string>();
  const zones: GardenZone[] = [];
  const placements: PlantPlacement[] = [];
  const gardenId = crypto.randomUUID();
  const species = new Map<string, Plant>();

  template.zones.forEach((spec, index) => {
    const zoneId = crypto.randomUUID();
    zones.push({
      id: zoneId,
      garden_id: gardenId,
      name: spec.name,
      color_pal: index % 6,
      x: spec.x,
      y: spec.y,
      width_cm: spec.width_cm,
      height_cm: spec.height_cm,
    });

    const main = chooseCropForZone(pool, spec, usedFamilies, usedIds);
    if (!main) return;
    usedFamilies.add(main.family);
    usedIds.add(main.id);

    const companion = pickCompanion(main, pool);
    const zonePlacements = fillZone(spec, zoneId, main, companion);
    placements.push(...zonePlacements);

    species.set(main.id, main);
    if (companion && zonePlacements.some((p) => p.plant_id === companion.id)) {
      species.set(companion.id, companion);
    }
  });

  return {
    garden: {
      id: gardenId,
      name: `Mon potager ${new Date().getFullYear()}`,
      season_year: new Date().getFullYear(),
      orientation: "N",
      width_cm: template.width_cm,
      height_cm: template.height_cm,
      zones,
      placements,
      supports: [],
      department,
    },
    department,
    climateZone,
    species: [...species.values()],
  };
}
